import { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, Filter, X, Users, Loader2, ChevronDown, ChevronUp, Trash2, Pencil,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';
import { TeamsTab } from './TeamsTab';
import { EmployeeOnboardingWizard } from './EmployeeOnboardingWizard'; // LBR2-C3

// LBR2-C3 — Active / Past / All lifecycle segregation (wired to hrRoutes GET
// /employees?view=, which composes C1's pure resolveEmployeeLifecycleWhere).
type LifecycleView = 'active' | 'past' | 'all';

type HrTab = 'employees' | 'teams';

interface Employee {
  id: string;
  employeeCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  designation?: string;
  departmentId?: string;
  department?: { id: string; name: string } | null;
  dateOfJoining?: string;
  salary?: number;
  panNumber?: string;
  status?: string;
  isActive?: boolean;
  createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-50 text-green-700',
  inactive: 'bg-red-50 text-red-700',
};

const INITIAL_FORM = {
  employeeCode: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  designation: '',
  departmentId: '',
  dateOfJoining: '',
  salary: '',
  panNumber: '',
};

export function EmployeesPage() {
  // Tab strip — defaults to Employees; URL-syncs via ?tab=teams.
  const [tab, setTab] = useState<HrTab>(() => {
    const q = new URLSearchParams(window.location.search).get('tab');
    return q === 'teams' ? 'teams' : 'employees';
  });
  useEffect(() => {
    const url = new URL(window.location.href);
    if (tab === 'teams') url.searchParams.set('tab', 'teams');
    else url.searchParams.delete('tab');
    window.history.replaceState({}, '', url.toString());
  }, [tab]);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  // LBR2-C3 — onboarding wizard (replaces the thin Add modal) + lifecycle view.
  const [showWizard, setShowWizard] = useState(false);
  const [viewFilter, setViewFilter] = useState<LifecycleView>('active');

  // 2026-09-05 — Edit opens the SAME five-step wizard as Add, prefilled. The old
  // six-field "Edit Employee" drawer is GONE: it could only touch name /
  // designation / department / salary / status, so KYC, PAN, Aadhaar, bank,
  // address, emergency contact, vehicle and documents could not be changed from
  // the UI at all once the person existed. It is deleted rather than left
  // unreachable — two forms writing the same record is how the two paths drifted
  // apart in the first place.
  const [editEmployeeId, setEditEmployeeId] = useState<string | null>(null);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      // LBR2-C3 — lifecycle view drives Active/Past/All; supersedes the legacy status filter.
      params.view = viewFilter;
      if (filterStatus) params.status = filterStatus;
      if (filterDepartment) params.departmentId = filterDepartment;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortDir = sortDir;
      params.page = String(page);
      params.limit = String(limit);
      const res = await api.get('/employees', { params });
      setEmployees(res.data.data?.employees ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [search, viewFilter, filterStatus, filterDepartment, sortField, sortDir, page]);

  useEffect(() => {
    fetchEmployees();
    api.get('/departments').then(res => setDepartments(res.data.data ?? [])).catch(() => {});
  }, [fetchEmployees]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/employees', {
        ...form,
        salary: form.salary ? parseFloat(form.salary) : undefined,
        departmentId: form.departmentId || undefined,
        panNumber: form.panNumber || undefined,
        phone: form.phone || undefined,
      });
      setShowModal(false);
      setForm(INITIAL_FORM);
      fetchEmployees();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create employee');
    } finally {
      setSaving(false);
    }
  };

  // LBR2-C2 — GUARDED delete (SEPARATE from Exit=archive). Segregates deletable
  // (wrongly-created, no activity) → front-end HIDE (reversible, no DB purge) vs
  // NOT deletable (has real data) → BLOCKED, steered to Exit. Two-phase: preview
  // the classification, then confirm only for a hide.
  const handleDelete = async (id: string) => {
    try {
      const preview = await api.get(`/hr-lifecycle/employees/${id}/deletability`);
      const { deletable, action, message } = preview.data?.data ?? {};
      if (!deletable || action === 'blocked') {
        // Real data present — hard delete is blocked. Inform + steer to Exit.
        alert(message || 'This employee has activity data and cannot be deleted. Use Exit to archive them instead.');
        return;
      }
      if (!confirm(message || 'This will hide this employee (reversible, no data deleted). Continue?')) return;
      await api.post(`/hr-lifecycle/employees/${id}/delete`, { confirm: true });
      fetchEmployees();
    } catch (err: any) {
      alert(err.response?.data?.error || err.response?.data?.message || 'Failed to delete employee');
    }
  };

  return (
    <div className="aura-root">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 28,
              color: 'var(--ink-0)',
              margin: 0,
            }}
          >
            {tab === 'teams' ? 'People' : 'Employees'}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
            {tab === 'teams'
              ? 'Employees and teams'
              : 'Manage your workforce'}
          </p>
        </div>
        {tab === 'employees' && (
          /* 🔒 Clear the edit id FIRST. Without it, Add after an Edit would
             reopen the wizard still bound to the last employee and PUT over
             them instead of creating anyone. */
          <button
            onClick={() => { setEditEmployeeId(null); setShowWizard(true); }}
            className="btn btn-primary"
          >
            <Plus size={14} />
            Add Employee
          </button>
        )}
      </div>

      {/* Tab strip */}
      <div
        className="row center"
        style={{
          gap: 4,
          marginBottom: 20,
          borderBottom: '1px solid var(--line)',
        }}
      >
        <TabButton
          label="Employees"
          active={tab === 'employees'}
          onClick={() => setTab('employees')}
        />
        <TabButton
          label="Teams"
          active={tab === 'teams'}
          onClick={() => setTab('teams')}
        />
      </div>

      {tab === 'teams' ? <TeamsTab /> : (
      <>
      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search employees..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        {/* LBR2-C3 — Active / Past / All lifecycle segregation toggle. */}
        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 text-sm">
          {(['active', 'past', 'all'] as LifecycleView[]).map((v) => (
            <button
              key={v}
              onClick={() => { setViewFilter(v); setPage(1); }}
              className={`rounded-md px-3 py-1.5 capitalize transition-colors ${viewFilter === v ? 'bg-[var(--ink-0)] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {v}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          {/* LBR2-C3 — Active/Past/All lifecycle status now lives in the segmented
              toggle above; department stays here. */}
          <select
            value={filterDepartment}
            onChange={(e) => { setFilterDepartment(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Departments</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          {(filterStatus || filterDepartment) && (
            <button
              onClick={() => { setFilterStatus(''); setFilterDepartment(''); }}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading employees...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchEmployees} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : employees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Users size={40} className="mb-2" />
            <p className="text-sm">No employees found</p>
            <p className="text-xs">Add your first employee to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('employeeCode')}>
                    <span className="flex items-center gap-1">Code <SortIcon field="employeeCode" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('firstName')}>
                    <span className="flex items-center gap-1">Name <SortIcon field="firstName" /></span>
                  </th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Department</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('dateOfJoining')}>
                    <span className="flex items-center gap-1">Date of Joining <SortIcon field="dateOfJoining" /></span>
                  </th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employees.map((emp) => (
                  <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{emp.employeeCode}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {emp.firstName} {emp.lastName}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{emp.email}</td>
                    <td className="px-4 py-3 text-gray-600">{emp.designation || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{emp.department?.name || departments.find(d => d.id === emp.departmentId)?.name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{emp.dateOfJoining ? formatDate(emp.dateOfJoining) : '-'}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const status = emp.status || (emp.isActive !== false ? 'active' : 'inactive');
                        return (
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setEditEmployeeId(emp.id); setShowWizard(true); }} className="rounded p-1 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)]" title="Edit"><Pencil size={14} /></button>
                        <button onClick={() => handleDelete(emp.id)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}
      </>
      )}

      {/* LBR2-C3 — Onboarding wizard (linked User+Employee + KYC + vehicle). */}
      <EmployeeOnboardingWizard
        open={showWizard}
        onClose={() => { setShowWizard(false); setEditEmployeeId(null); }}
        onCreated={fetchEmployees}
        employeeId={editEmployeeId}
      />

      {/* Create Employee Modal (legacy — superseded by the wizard; kept dormant) */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Add Employee</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Employee Code *</label>
                <input
                  type="text"
                  value={form.employeeCode}
                  onChange={(e) => setForm({ ...form, employeeCode: e.target.value })}
                  placeholder="EMP-001"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">First Name *</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Last Name *</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Designation</label>
                  <input
                    type="text"
                    value={form.designation}
                    onChange={(e) => setForm({ ...form, designation: e.target.value })}
                    placeholder="e.g. Software Engineer"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Department</label>
                  <select
                    value={form.departmentId}
                    onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">Select Department</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Date of Joining *</label>
                  <input
                    type="date"
                    value={form.dateOfJoining}
                    onChange={(e) => setForm({ ...form, dateOfJoining: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Salary</label>
                  <input
                    type="number"
                    value={form.salary}
                    onChange={(e) => setForm({ ...form, salary: e.target.value })}
                    placeholder="Monthly CTC"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">PAN Number</label>
                <input
                  type="text"
                  value={form.panNumber}
                  onChange={(e) => setForm({ ...form, panNumber: e.target.value })}
                  placeholder="AAAAA0000A"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !form.employeeCode || !form.firstName || !form.lastName || !form.email || !form.dateOfJoining}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Employee
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Tab strip button (warm-ivory, saffron for active) ───────────────────────
function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        border: 'none',
        background: active ? 'var(--saffron-50)' : 'transparent',
        color: active ? 'var(--saffron-600)' : 'var(--ink-2)',
        fontFamily: 'var(--ff-sans)',
        fontSize: 13,
        fontWeight: 500,
        padding: '8px 16px',
        borderTopLeftRadius: 'var(--r-sm)',
        borderTopRightRadius: 'var(--r-sm)',
        borderBottom: active
          ? '2px solid var(--saffron)'
          : '2px solid transparent',
        marginBottom: -1,
        cursor: 'pointer',
        transition: 'background 0.12s, color 0.12s, border-color 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!active)
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-0)';
      }}
      onMouseLeave={(e) => {
        if (!active)
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-2)';
      }}
    >
      {label}
    </button>
  );
}
